import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { aiProcessingService } from '../api/authService';

const AI_JOBS_STORAGE_KEY = 'ai-pending-jobs';
const POLL_INTERVAL_MS = 4000;
const TOAST_AUTO_DISMISS_MS = 8000;

const AiJobsContext = createContext(null);

export const useAiJobs = () => {
    const ctx = useContext(AiJobsContext);
    if (!ctx) {
        throw new Error('useAiJobs يجب أن يُستخدم داخل AiJobsProvider');
    }
    return ctx;
};

const readJobsFromStorage = () => {
    try {
        return JSON.parse(localStorage.getItem(AI_JOBS_STORAGE_KEY) || '[]');
    } catch {
        return [];
    }
};

const writeJobsToStorage = (jobs) => {
    try {
        localStorage.setItem(AI_JOBS_STORAGE_KEY, JSON.stringify(jobs));
    } catch (err) {
        console.error('Failed to persist AI jobs:', err);
    }
};

const mergeResultIntoDraft = (job, data) => {
    try {
        const draftRaw = localStorage.getItem(job.draftKey);
        const draft = draftRaw ? JSON.parse(draftRaw) : {};
        draft.__pendingAiResult = {
            data,
            jobId: job.database_id,
        };
        localStorage.setItem(job.draftKey, JSON.stringify(draft));
    } catch (err) {
        console.error('Failed to merge AI result into draft:', err);
    }
};

export const AiJobsProvider = ({ children }) => {
    const [jobs, setJobs] = useState(() => readJobsFromStorage());
    const [notifications, setNotifications] = useState([]);
    const intervalRef = useRef(null);
    const inFlightRef = useRef(false);

    const registerJob = useCallback((job) => {
        setJobs(prev => {
            const next = [...prev, { ...job, status: 'IN_QUEUE' }];
            writeJobsToStorage(next);
            return next;
        });
    }, []);

    const dismissNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const addNotification = useCallback((type, message) => {
        const id = `${Date.now()}-${Math.random()}`;
        setNotifications(prev => [...prev, { id, type, message }]);
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {});
        }
    }, []);

    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = setInterval(async () => {
            if (inFlightRef.current) return;

            const currentJobs = readJobsFromStorage();
            if (currentJobs.length === 0) return;

            inFlightRef.current = true;
            let remaining = [...currentJobs];
            let stateUpdated = false;

            for (const job of currentJobs) {
                try {
                    const res = await aiProcessingService.checkStatusOnce(job.database_id);

                    if (res.status === 'COMPLETED') {
                        mergeResultIntoDraft(job, res.data);

                        addNotification('success', `✅ اكتمل تحليل ${job.label || job.type}`);

                        if (window.Notification && Notification.permission === 'granted') {
                            try {
                                new Notification('اكتمل التحليل', { body: job.label || job.type });
                            } catch { /* ignore error */ }
                        }

                        remaining = remaining.filter(j => j.database_id !== job.database_id);
                        stateUpdated = true;
                    } else if (res.status === 'FAILED') {
                        addNotification('error', `❌ فشل تحليل ${job.label || job.type}${res.error ? `: ${res.error}` : ''}`);

                        remaining = remaining.filter(j => j.database_id !== job.database_id);
                        stateUpdated = true;
                    }
                } catch (err) {
                    console.warn('AiJobsProvider: فشل الاستعلام عن حالة المهمة', job.database_id, err);
                }
            }

            if (stateUpdated) {
                writeJobsToStorage(remaining);
                setJobs(remaining);
            }
            inFlightRef.current = false;
        }, POLL_INTERVAL_MS);

        return () => clearInterval(intervalRef.current);
    }, [addNotification]);

    return (
        <AiJobsContext.Provider value={{ jobs, registerJob, notifications, dismissNotification }}>
            {children}
            {/* 🆕 مؤشر عائم ثابت يظهر في أي صفحة طول ما فيه مهمة تحليل شغالة بالخلفية */}
            <AiJobsFloatingIndicator jobs={jobs} />
            <AiJobsToastOverlay notifications={notifications} onDismiss={dismissNotification} />
        </AiJobsContext.Provider>
    );
};

// 🆕 مؤشر عائم صغير (Floating Pill) — يظهر أسفل يسار الشاشة في أي مكان بالتطبيق
// طول ما فيه job واحد أو أكتر IN_QUEUE أو قيد التنفيذ، مع Spinner بسيط وعدد المهام
const AI_INDICATOR_KEYFRAMES = `
@keyframes aiIndicatorSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}
@keyframes aiIndicatorPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
}
`;

const AiJobsFloatingIndicator = ({ jobs }) => {
    if (!jobs || jobs.length === 0) return null;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: '20px',
                insetInlineStart: '20px', // يسار في LTR، يمين في RTL تلقائياً
                zIndex: 99998,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                backgroundColor: '#1E293B',
                color: '#fff',
                padding: '10px 16px',
                borderRadius: '999px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                fontSize: '13px',
                fontWeight: 600,
                direction: 'rtl',
                animation: 'aiIndicatorPulse 2s ease-in-out infinite',
            }}
        >
            <style>{AI_INDICATOR_KEYFRAMES}</style>
            <span
                style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: '2.5px solid rgba(255,255,255,0.25)',
                    borderTopColor: '#60A5FA',
                    animation: 'aiIndicatorSpin 0.8s linear infinite',
                    flexShrink: 0,
                }}
            />
            <span>
                🤖 {jobs.length === 1
                    ? 'جاري تحليل مستند بالذكاء الاصطناعي...'
                    : `جاري تحليل ${jobs.length} مستندات بالذكاء الاصطناعي...`}
            </span>
        </div>
    );
};

const AiJobsToastOverlay = ({ notifications, onDismiss }) => {
    if (notifications.length === 0) return null;

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            insetInlineEnd: '20px',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            maxWidth: '340px',
        }}>
            {notifications.map(n => (
                <ToastItem key={n.id} notification={n} onDismiss={onDismiss} />
            ))}
        </div>
    );
};

// 🆕 مكون خاص بكل Toast لإدارة المؤقت بشكل مستقل وبدون مشاكل في الـ Cleanup
const ToastItem = ({ notification, onDismiss }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(notification.id);
        }, TOAST_AUTO_DISMISS_MS);

        return () => clearTimeout(timer);
    }, [notification.id, onDismiss]);

    return (
        <div
            onClick={() => onDismiss(notification.id)}
            style={{
                backgroundColor: notification.type === 'success' ? '#16A34A' : '#DC2626',
                color: '#fff',
                padding: '14px 18px',
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
                direction: 'rtl',
                animation: 'aiJobToastIn 0.25s ease-out',
            }}
        >
            {notification.message}
            <div style={{ fontSize: '11px', fontWeight: 400, opacity: 0.85, marginTop: '4px' }}>
                اضغط للإغلاق
            </div>
            <style>{`
                @keyframes aiJobToastIn {
                    from { transform: translateY(-12px); opacity: 0; }
                    to   { transform: translateY(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
};